import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Capacitor } from "@capacitor/core";
import ProtectedRoute from "@/components/ProtectedRoute";
import MobileTabBar from "@/components/MobileTabBar";
import RouteSeo from "@/components/RouteSeo";
// İlk ekranlar (web: landing, uygulama: kayıt/giriş) anında açılsın diye eager.
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Geri kalan tüm sayfalar LAZY: kod, sayfa ilk ziyaret edildiğinde yüklenir.
// Tek parça ~1.1MB bundle webview'da açılışı ve etkileşimi yavaşlatıyordu;
// bölmek native uygulamadaki takılmaların ana ilacı.
const Booking = lazy(() => import("@/pages/Booking"));
const LocalLanding = lazy(() => import("@/pages/LocalLanding"));
const Account = lazy(() => import("@/pages/Account"));
const CustomerBookings = lazy(() => import("@/pages/customer/Bookings"));
const CustomerBookingDetail = lazy(() => import("@/pages/customer/BookingDetail"));
const CustomerTalepler = lazy(() => import("@/pages/customer/Talepler"));
const CustomerTalepDetail = lazy(() => import("@/pages/customer/TalepDetail"));
const CustomerMesajlar = lazy(() => import("@/pages/customer/Mesajlar"));
const CustomerSupport = lazy(() => import("@/pages/customer/Support"));
const CustomerAddresses = lazy(() => import("@/pages/customer/Addresses"));
const PanelLayout = lazy(() => import("@/pages/panel/PanelLayout"));
const Dashboard = lazy(() => import("@/pages/panel/Dashboard"));
const Requests = lazy(() => import("@/pages/panel/Requests"));
const MyOffers = lazy(() => import("@/pages/panel/MyOffers"));
const Cuzdan = lazy(() => import("@/pages/panel/Cuzdan"));
const PanelMesajlar = lazy(() => import("@/pages/panel/Mesajlar"));
const Jobs = lazy(() => import("@/pages/panel/Jobs"));
const CalendarPage = lazy(() => import("@/pages/panel/CalendarPage"));
const Reviews = lazy(() => import("@/pages/panel/Reviews"));
const Support = lazy(() => import("@/pages/panel/Support"));
const Profile = lazy(() => import("@/pages/panel/Profile"));
const Tarife = lazy(() => import("@/pages/panel/Tarife"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Overview = lazy(() => import("@/pages/admin/Overview"));
const AdminProviders = lazy(() => import("@/pages/admin/Providers"));
const AdminCustomers = lazy(() => import("@/pages/admin/Customers"));
const AdminTalepler = lazy(() => import("@/pages/admin/Talepler"));
const AdminDisputes = lazy(() => import("@/pages/admin/Disputes"));
const AdminReportedReviews = lazy(() => import("@/pages/admin/ReportedReviews"));
const AdminSupport = lazy(() => import("@/pages/admin/Support"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminServices = lazy(() => import("@/pages/admin/Services"));
const AdminSiteContent = lazy(() => import("@/pages/admin/SiteContent"));
const AdminTimeSlots = lazy(() => import("@/pages/admin/TimeSlots"));
const AdminKontor = lazy(() => import("@/pages/admin/KontorYonetimi"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Lazy sayfa yüklenirken gösterilen kısa dolgu ekranı.
function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="h-10 w-10 rounded-full border-4 border-emerald-200 border-t-[#10B981] animate-spin" />
    </div>
  );
}

// Eski bildirim linkleri /rezervasyon/{n} → doğru müşteri rotası /rezervasyonlarim/{n}
function RezervasyonRedirect() {
  const { number } = useParams();
  return <Navigate to={`/rezervasyonlarim/${number}`} replace />;
}

// Native uygulamada rota değişince sayfayı yumuşakça belirt (uygulama geçiş hissi).
// Yalnız opaklık animasyonu — transform fixed alt çubuğu bozardı.
function NativePageFx() {
  const location = useLocation();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Android WebView'da animasyon katmanı bazı raster karolarını boş bırakıp
    // metinleri "kırpılmış" gösterebiliyor (emülatörde belirgin) — Android'de kapalı.
    if (Capacitor.getPlatform() === "android") return;
    const el = document.querySelector(".App");
    if (!el) return;
    el.classList.remove("native-page-enter");
    // Animasyonu yeniden tetiklemek için reflow zorla.
    void el.offsetWidth;
    el.classList.add("native-page-enter");
  }, [location.pathname]);
  return null;
}

// Ana sayfa: native uygulamada, oturum açılmamışsa doğrudan giriş ekranına git
// (uygulamayı zaten PAK'ı bilenler indirir; pazarlama landing'i web'e özgü).
// Web'de her zaman Landing gösterilir.
function HomeRoute() {
  const { user, loading } = useAuth();
  if (Capacitor.isNativePlatform()) {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
          <div className="h-10 w-10 rounded-full border-4 border-emerald-200 border-t-[#10B981] animate-spin" />
        </div>
      );
    }
    if (!user) return <Navigate to="/kayit" replace />;
    // Uygulamada landing yok: herkes doğrudan kendi ana ekranına gider.
    if (user.role === "saglayici") return <Navigate to="/panel" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/taleplerim" replace />; // müşteri
  }
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteSeo />
          <NativePageFx />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/rezervasyon" element={<Booking />} />
            <Route path="/rezervasyon/:number" element={<RezervasyonRedirect />} />
            <Route path="/hizmet/:service/:il" element={<LocalLanding />} />
            <Route path="/hizmet/:service/:il/:ilce" element={<LocalLanding />} />
            <Route path="/giris" element={<Login />} />
            <Route path="/kayit" element={<Register />} />
            <Route
              path="/panel"
              element={
                <ProtectedRoute role="saglayici">
                  <PanelLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="talepler" element={<Requests />} />
              <Route path="tekliflerim" element={<MyOffers />} />
              <Route path="cuzdan" element={<Cuzdan />} />
              <Route path="mesajlar" element={<PanelMesajlar />} />
              <Route path="mesajlar/:id" element={<PanelMesajlar />} />
              <Route path="isler" element={<Jobs />} />
              <Route path="takvim" element={<CalendarPage />} />
              <Route path="yorumlar" element={<Reviews />} />
              <Route path="destek" element={<Support />} />
              <Route path="profil" element={<Profile />} />
              <Route path="tarife" element={<Tarife />} />
            </Route>
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="saglayicilar" element={<AdminProviders />} />
              <Route path="musteriler" element={<AdminCustomers />} />
              <Route path="talepler" element={<AdminTalepler />} />
              <Route path="anlasmazliklar" element={<AdminDisputes />} />
              <Route path="bildirilen-yorumlar" element={<AdminReportedReviews />} />
              <Route path="destek" element={<AdminSupport />} />
              <Route path="hizmetler" element={<AdminServices />} />
              <Route path="icerik" element={<AdminSiteContent />} />
              <Route path="saatler" element={<AdminTimeSlots />} />
              <Route path="kontor" element={<AdminKontor />} />
              <Route path="ayarlar" element={<AdminSettings />} />
            </Route>
            <Route
              path="/hesabim"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rezervasyonlarim"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/taleplerim"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerTalepler />
                </ProtectedRoute>
              }
            />
            <Route
              path="/taleplerim/:number"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerTalepDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mesajlarim"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerMesajlar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mesajlarim/:id"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerMesajlar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/destek"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerSupport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/adreslerim"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerAddresses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rezervasyonlarim/:number"
              element={
                <ProtectedRoute role="musteri">
                  <CustomerBookingDetail />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <MobileTabBar />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
